// Gallery API - Server-side storage for truly live galleries
import { Gallery } from '../types/Gallery';
import { API_BASE_URL } from './apiBase';

const isProduction = process.env.NODE_ENV === 'production';

const loadGalleriesFromLocal = (): Gallery[] => {
  try {
    // Prefer the "live" key if present, but support legacy keys too
    const candidates = [
      'carmen_live_galleries', // LiveGalleryStorage wrapper object
      'admin_galleries',
      'react_galleries',
    ];

    for (const key of candidates) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;

      const parsed = JSON.parse(raw);
      // carmen_live_galleries stores { galleries, lastUpdated, version }
      const maybeGalleries = Array.isArray(parsed) ? parsed : parsed?.galleries;
      if (Array.isArray(maybeGalleries)) return maybeGalleries as Gallery[];
    }

    return [];
  } catch (error) {
    console.error('Error loading galleries from localStorage:', error);
    return [];
  }
};

const createGallerySaveId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `save_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};

// Save all galleries to server with retry logic.
// The save id stays the same across retries so a timed-out request joins the
// original write instead of inserting the gallery list a second time.
export const saveGalleriesToServer = async (
  galleries: Gallery[],
  retryCount = 0,
  saveId = createGallerySaveId(),
): Promise<boolean> => {
  const maxRetries = isProduction ? 3 : 2; // More retries in production
  
  // Create abort controller for timeout in production (declare outside try for catch block access)
  const controller = isProduction ? new AbortController() : null;
  let timeoutId: NodeJS.Timeout | null = null;
  
  try {
    const totalImages = galleries.reduce((sum, g) => sum + (g.images?.length || 0), 0);
    const payloadSize = JSON.stringify(galleries).length;
    console.log(`💾 Saving galleries to server: ${galleries.length} galleries, ${totalImages} total images, ${(payloadSize / 1024).toFixed(2)} KB (attempt ${retryCount + 1}/${maxRetries + 1})`);
    
    // Check payload size (some servers have limits)
    if (payloadSize > 10 * 1024 * 1024) { // 10MB
      console.warn(`⚠️ Large payload size: ${(payloadSize / 1024 / 1024).toFixed(2)} MB - may cause issues`);
    }
    
    // Set timeout in production
    if (isProduction && controller) {
      timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
    }
    
    const response = await fetch(`${API_BASE_URL}/galleries`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Gallery-Save-Id': saveId,
      },
      body: JSON.stringify(galleries),
      signal: controller?.signal,
    });
    
    if (timeoutId) clearTimeout(timeoutId);
    
    if (!response.ok) {
      let errorText = 'Unknown error';
      try {
        errorText = await response.text();
      } catch (e) {
        console.error('Could not read error response:', e);
      }
      
      console.error(`❌ Server save failed: HTTP ${response.status}`);
      console.error(`❌ Error response: ${errorText}`);
      
      // Retry on 500 errors or network issues
      if ((response.status === 500 || response.status >= 502 || response.status === 0) && retryCount < maxRetries) {
        const delay = (retryCount + 1) * 2000; // Exponential backoff: 2s, 4s, 6s
        console.log(`🔄 Retrying server save in ${delay/1000} seconds... (${retryCount + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return saveGalleriesToServer(galleries, retryCount + 1, saveId);
      }
      
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText.substring(0, 200)}`);
    }
    
    const result = await response.json();
    if (result.success) {
      const storage = result.storage || 'unknown';
      if (storage !== 'mongodb') {
        console.error(`❌ Server accepted save but storage backend is "${storage}" (expected "mongodb")`, result);
        return false;
      }
      console.log('✅ Server saved galleries to MongoDB successfully:', result);
      clearLiveGalleryCache();
      return true;
    }

    console.error('❌ Server returned success=false:', result);
    return false;
  } catch (error) {
    console.error('❌ Error saving galleries to server:', error);
    if (error instanceof Error) {
      console.error('❌ Error details:', error.message);
      console.error('❌ Error name:', error.name);
    }
    
    // Clean up timeout if it exists
    if (timeoutId) clearTimeout(timeoutId);
    
    // Retry on network errors or timeouts
    if (retryCount < maxRetries && (
      error instanceof TypeError || 
      (error instanceof Error && (error.name === 'AbortError' || error.message.includes('fetch')))
    )) {
      const delay = (retryCount + 1) * 2000; // Exponential backoff
      console.log(`🔄 Retrying server save in ${delay/1000} seconds... (${retryCount + 1}/${maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return saveGalleriesToServer(galleries, retryCount + 1, saveId);
    }
    
    return false;
  }
};

export const syncGalleriesWithCloudinary = async (): Promise<{
  success: boolean;
  message: string;
  removedImages?: number;
  removedGalleries?: number;
  changedGalleries?: number;
  beforeCount?: number;
  afterCount?: number;
}> => {
  try {
    const response = await fetch(`${API_BASE_URL}/galleries/sync-cloudinary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    const body = await response.json();
    if (!response.ok) {
      throw new Error(body?.message || `Sync failed (${response.status})`);
    }

    clearLiveGalleryCache();
    return {
      success: true,
      message: body.message || 'Sync completed.',
      removedImages: body.removedImages,
      removedGalleries: body.removedGalleries,
      changedGalleries: body.changedGalleries,
      beforeCount: body.beforeCount,
      afterCount: body.afterCount,
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to sync galleries with Cloudinary',
    };
  }
};

const allowLocalFallback = process.env.REACT_APP_ALLOW_LOCAL_FALLBACK === 'true';

export type GalleryLoadView = 'full' | 'cards';

const galleryCategoryList = (gallery: { category?: string; categories?: string[] }): string[] => {
  const seen = new Set<string>();
  const slugs: string[] = [];
  [gallery.category, ...(Array.isArray(gallery.categories) ? gallery.categories : [])].forEach((value) => {
    const slug = String(value || '').trim();
    const key = slug.toLowerCase();
    if (!slug || seen.has(key)) return;
    seen.add(key);
    slugs.push(slug);
  });
  return slugs;
};

const galleryCache = new Map<GalleryLoadView, Gallery[]>();
const galleryLoads = new Map<GalleryLoadView, Promise<Gallery[]>>();
const galleryByIdCache = new Map<string, Gallery>();
const galleryByIdLoads = new Map<string, Promise<Gallery | null>>();
let galleryEpoch = 0;

export const clearLiveGalleryCache = () => {
  galleryEpoch += 1;
  galleryCache.clear();
  galleryLoads.clear();
  galleryByIdCache.clear();
  galleryByIdLoads.clear();
};

const formatServerGallery = (gallery: any): Gallery => ({
  id: gallery.id,
  name: gallery.name,
  description: gallery.description || '',
  excerpt: gallery.excerpt || '',
  images: gallery.images || [],
  imageCount: typeof gallery.imageCount === 'number' ? gallery.imageCount : (gallery.images?.length || 0),
  heroImage: gallery.heroImage || (gallery.images && gallery.images[0]) || {
    publicId: 'placeholder',
    url: '/placeholder.jpg',
    alt: gallery.name,
    width: 400,
    height: 300
  },
  heroImageIndex: gallery.heroImageIndex || 0,
  heroImagePosition: gallery.heroImagePosition || 'center',
  heroImageZoom: gallery.heroImageZoom || 1.0,
  heroImageCustomX: gallery.heroImageCustomX || 50,
  heroImageCustomY: gallery.heroImageCustomY || 50,
  heroImageCropBox: gallery.heroImageCropBox,
  category: gallery.category || 'portraits',
  categories: galleryCategoryList(gallery),
  galleryType: gallery.galleryType || 'mosaic',
  published: gallery.published !== undefined ? gallery.published : true,
  createdAt: gallery.createdAt || new Date().toISOString(),
  updatedAt: gallery.updatedAt || new Date().toISOString(),
  order: gallery.order || 1
});

// Get all galleries from server with retry for spindown
export const getGalleriesFromServer = async (retryCount = 0, view: GalleryLoadView = 'full'): Promise<Gallery[]> => {
  try {
    const cacheBuster = Date.now();
    const endpoint = view === 'cards'
      ? `${API_BASE_URL}/galleries?view=cards&_cb=${cacheBuster}`
      : `${API_BASE_URL}/galleries?_cb=${cacheBuster}`;
    console.log(`📱 Fetching ${view} galleries from server... (attempt ${retryCount + 1})`);
    const response = await fetch(endpoint, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    
    if (!response.ok) {
      if (response.status === 404 && retryCount < 2) {
        console.log('🔄 Server might be spinning up, retrying in 5 seconds...');
        await new Promise(resolve => setTimeout(resolve, 5000));
        return getGalleriesFromServer(retryCount + 1, view);
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const galleries = await response.json();
    console.log('📱 Server returned galleries:', Array.isArray(galleries) ? galleries.length : 0);
    if (!Array.isArray(galleries)) return [];
    return galleries.map(formatServerGallery);
  } catch (error) {
    console.error('Error fetching galleries from server:', error);
    return [];
  }
};

export const getGalleryById = async (id: string): Promise<Gallery | null> => {
  const key = String(id);
  const cached = galleryByIdCache.get(key);
  if (cached) return cached;
  const pending = galleryByIdLoads.get(key);
  if (pending) return pending;

  const request = (async () => {
    const response = await fetch(`${API_BASE_URL}/galleries/${encodeURIComponent(key)}`);
    if (!response.ok) return null;
    const gallery = await response.json();
    if (!gallery || gallery.error) return null;
    const formatted = formatServerGallery(gallery);
    galleryByIdCache.set(key, formatted);
    return formatted;
  })().finally(() => {
    galleryByIdLoads.delete(key);
  });

  galleryByIdLoads.set(key, request);
  return request;
};

// Load galleries from canonical API source (Mongo-backed on server)
export const loadLiveGalleries = async (view: GalleryLoadView = 'full'): Promise<Gallery[]> => {
  const cached = galleryCache.get(view);
  if (cached) return cached;
  const pending = galleryLoads.get(view);
  if (pending) return pending;

  const epoch = galleryEpoch;
  const current: { request?: Promise<Gallery[]> } = {};
  const request = (async () => {
    try {
      console.log(`🌐 Loading ${view} galleries from server source of truth...`);
      const serverGalleries = await getGalleriesFromServer(0, view);
      if (epoch !== galleryEpoch) {
        if (galleryLoads.get(view) === current.request) galleryLoads.delete(view);
        return loadLiveGalleries(view);
      }
      if (serverGalleries.length > 0) {
        galleryCache.set(view, serverGalleries);
        return serverGalleries;
      }
      if (allowLocalFallback) {
        const localGalleries = loadGalleriesFromLocal();
        if (localGalleries.length > 0) {
          console.warn('⚠️ Local fallback enabled: returning galleries from localStorage');
          return localGalleries;
        }
      }
      return [];
    } catch (error) {
      console.error('❌ Error loading galleries from server:', error);
      if (epoch !== galleryEpoch) {
        if (galleryLoads.get(view) === current.request) galleryLoads.delete(view);
        return loadLiveGalleries(view);
      }
      if (allowLocalFallback) {
        const localGalleries = loadGalleriesFromLocal();
        if (localGalleries.length > 0) {
          console.warn('⚠️ Local fallback enabled: using localStorage due to server error');
          return localGalleries;
        }
      }
      return [];
    } finally {
      if (galleryLoads.get(view) === current.request) galleryLoads.delete(view);
    }
  })();
  current.request = request;

  galleryLoads.set(view, request);
  return request;
};
